import { clockMinutes } from '@voll-intelligence/dates';
import { carrierNameOf, type FlightOfferSource } from './flight_offer_source.js';

/**
 * Lógica pura do filtro unificado de voos (drawer do chat + coluna da bancada):
 * estado de filtro + derivação da lista exibida (filter + sort) + as derivações
 * de opção do protótipo da bancada (contagens por eixo, teto de política,
 * companhias com faixa de preço, aeroportos por cidade). Sem React, sem estado
 * duplicado — os hosts mantêm só `FlightFilterState` e derivam tudo.
 *
 * `depMin`/duração/companhia saem dos campos da oferta; paradas caem para
 * `segments.length - 1` quando o provider não traz `stops`.
 */

/** Os cortes que o servidor aplica — o `ModalSessionFlightCuts` do domain pela
 *  forma (o genui-kit prova a compatibilidade num teste de tipo). */
export interface FlightServerCuts {
  stops?: 'nonstop' | 'up_to_1' | 'up_to_2';
  airlines?: string[];
  minPrice?: number;
  maxPrice?: number;
  /** Número do voo em DÍGITOS ("2084"). */
  number?: string;
  departureWindows?: { start: string; end: string }[];
  withinPolicyOnly?: boolean;
  avoidAirports?: string[];
  viaAirports?: string[];
}

/** A ordenação pedida ao servidor (`ModalSessionFlightOrder`). */
export type FlightServerOrder =
  | 'recommended'
  | 'cheapest'
  | 'fastest'
  | 'earliest'
  | 'earlier_arrival';

/** A cidade de um IATA — o host passa o `cityByAirport` do domain. */
export type CityOfAirport = (iata: string) => string | null | undefined;

/** `rec` (Recomendado) é o default do explorer: composição servida pelos badges
 *  do fio (#2108) sobre uma base de preço crescente. `arr` ordena pela CHEGADA. */
export type SortKey = 'rec' | 'price' | 'dep' | 'dur' | 'arr';
/**
 * As opções do eixo de paradas (#2111) — checkboxes de MULTI-seleção unidas por
 * OU. Os três tetos falam o vocabulário do fio (`ModalSessionFlightCuts.stops`);
 * `more_than_2` é a opção que o provider não filtra e corta só no cliente.
 */
export type StopsChoice = 'nonstop' | 'up_to_1' | 'up_to_2' | 'more_than_2';

const STOPS_CHOICES: readonly StopsChoice[] = ['nonstop', 'up_to_1', 'up_to_2', 'more_than_2'];

const STOPS_PRED: Record<StopsChoice, (stops: number) => boolean> = {
  nonstop: (stops) => stops === 0,
  up_to_1: (stops) => stops <= 1,
  up_to_2: (stops) => stops <= 2,
  more_than_2: (stops) => stops > 2,
};

/**
 * Faixa de horário da régua (#2112), em MINUTOS do dia `[início, fim]`,
 * inclusiva nas duas pontas. `[0, 1439]` (00:00–23:59) é o eixo neutro — a
 * régua cheia não corta nada e não viaja no fio.
 */
export type DayMinuteRange = [number, number];

/** O último minuto do dia (23:59) — o teto das réguas de horário. */
export const DAY_MINUTES_END = 23 * 60 + 59;

const fullDayRange = (): DayMinuteRange => [0, DAY_MINUTES_END];

const isFullDay = (r: DayMinuteRange): boolean => r[0] <= 0 && r[1] >= DAY_MINUTES_END;

/** Minuto do dia → `HH:mm` — o formato de `departureWindows` no fio e o texto
 *  legível da régua ("06:00 – 11:59"). */
export const formatDayMinute = (minute: number): string => {
  const h = String(Math.floor(minute / 60)).padStart(2, '0');
  const m = String(minute % 60).padStart(2, '0');
  return `${h}:${m}`;
};
export interface FlightFilterState {
  sort: SortKey;
  /** Opções de paradas marcadas, unidas por OU. Vazio = qualquer. */
  stops: StopsChoice[];
  /** Códigos IATA de companhia selecionados. Vazio = todas. */
  airlines: string[];
  /** IATA de aeroporto em QUALQUER ponta do voo (ida e volta). Vazio = todos. */
  airports: string[];
  /** Faixa da PARTIDA da ida (régua de dois polegares). Cheia = qualquer hora. */
  departureRange: DayMinuteRange;
  /** Faixa da CHEGADA da ida — em corporativo a hora que decide é a de
   *  chegar. Cheia = qualquer hora. */
  arrivalRange: DayMinuteRange;
  /** Só ofertas com veredito `policy.status === 'in'` (sem dado ⇒ cortada —
   *  a opção zera e desabilita quando o produtor não manda política). */
  withinPolicyOnly: boolean;
  /** Piso da régua de preço (#2110). `0` = sem piso (preço é sempre > 0). */
  minPrice: number;
  maxPrice: number;
  /** Número do voo digitado ("LA 2084"). Match EXATO tolerante a espaço e
   *  caixa contra carrier+número ou só o número. Vazio = eixo neutro. */
  flightNumber: string;
  /** IATAs de CONEXÃO a evitar (chip "sem conexão em X"). Vazio = sem corte. Chip-only:
   *  não tem controle no painel; viaja no fio (`avoidAirports`) e espelha na página. */
  avoidAirports: string[];
  /** IATAs de CONEXÃO exigidos — pelo menos um (chip "com conexão em X"). Vazio = sem corte. */
  viaAirports: string[];
}

const priceOf = (o: FlightOfferSource) => o.price.amount;
const stopsOf = (o: FlightOfferSource) => o.stops ?? o.segments.length - 1;
const depOf = (o: FlightOfferSource) => clockMinutes(o.segments[0]?.departAt ?? '');
const arrOf = (o: FlightOfferSource) => clockMinutes(o.segments.at(-1)?.arriveAt ?? '');
const durOf = (o: FlightOfferSource) =>
  o.segments.reduce((sum, s) => sum + (s.durationMinutes ?? 0), 0);
const allSegments = (o: FlightOfferSource) => [...o.segments, ...(o.returnLeg?.segments ?? [])];
/** Companhias do voo inteiro — basta UMA perna na companhia escolhida (no
 *  combo, ida e volta podem ser de operadoras diferentes). */
const carriersOf = (o: FlightOfferSource) => [...new Set(allSegments(o).map((s) => s.carrier))];
/** Todos os aeroportos que o voo toca, nas duas pontas de cada segmento. */
const airportsOf = (o: FlightOfferSource) => [
  ...new Set(allSegments(o).flatMap((s) => [s.from, s.to])),
];

export type AirportSide = 'from' | 'to' | 'both' | 'unknown';

/** Em que PONTA cada aeroporto aparece nas ofertas em cena (ida e volta, como `airportsOf`): decide o
 *  rótulo do chip ("Saindo de" × "Chegando em" × "Só"). */
export function airportSidesOf(
  offers: readonly FlightOfferSource[],
): (iata: string) => AirportSide {
  const froms = new Set<string>();
  const tos = new Set<string>();
  for (const offer of offers) {
    for (const leg of [offer.segments, offer.returnLeg?.segments ?? []]) {
      if (leg.length === 0) continue;
      const first = leg[0]?.from;
      const last = leg.at(-1)?.to;
      if (first) froms.add(first);
      if (last) tos.add(last);
    }
  }
  return (iata) => {
    const from = froms.has(iata);
    const to = tos.has(iata);
    if (from && to) return 'both';
    if (to) return 'to';
    if (from) return 'from';
    return 'unknown';
  };
}

/** Numa ida-e-volta a OUTRA perna é o espelho da ativa: quem parte na ida chega na volta. `both`/
 *  `unknown` não têm o que espelhar e seguem como estão. */
export function mirrorAirportSide(side: AirportSide): AirportSide {
  if (side === 'from') return 'to';
  if (side === 'to') return 'from';
  return side;
}

/** Aeroportos de CONEXÃO do voo inteiro: o destino de cada segmento menos o último de cada perna. */
const connectionsOf = (o: FlightOfferSource) => [
  ...new Set([
    ...o.segments.slice(0, -1).map((s) => s.to),
    ...(o.returnLeg?.segments.slice(0, -1).map((s) => s.to) ?? []),
  ]),
];

/**
 * Teto do eixo de preço. `ceiling` é o teto do CONJUNTO da busca; sem ele cai na
 * lista em mão.
 *
 * A distinção só aparece no caso que quebra: quando é o próprio corte de preço que
 * esvazia a lista, derivar da lista dá `0` — e aí o teto do slider, o predicado de
 * "eixo ativo" e o alvo do Redefinir apagam justamente o corte que precisa ser
 * desfeito. O corte não pode destruir o dado que o desfaz.
 */
const ceilingOf = (offers: readonly FlightOfferSource[], ceiling?: number): number =>
  ceiling ?? priceBounds(offers)[1];

/** Piso do eixo de preço — o espelho de `ceilingOf`, para a régua de duas
 *  pontas (#2110). */
const floorOf = (offers: readonly FlightOfferSource[], floor?: number): number =>
  floor ?? priceBounds(offers)[0];

/** Estado inicial derivado da lista: sem cortes, `maxPrice` = maior preço. */
export function defaultFilterState(
  offers: readonly FlightOfferSource[],
  ceiling?: number,
): FlightFilterState {
  const max = ceilingOf(offers, ceiling);
  return {
    sort: 'rec',
    stops: [],
    airlines: [],
    airports: [],
    departureRange: fullDayRange(),
    arrivalRange: fullDayRange(),
    withinPolicyOnly: false,
    minPrice: 0,
    maxPrice: max,
    flightNumber: '',
    avoidAirports: [],
    viaAirports: [],
  };
}

/** Limites do range de preço `[min, max]` da lista (piso/teto do slider). */
export function priceBounds(offers: readonly FlightOfferSource[]): [number, number] {
  if (offers.length === 0) return [0, 0];
  const prices = offers.map(priceOf);
  return [Math.floor(Math.min(...prices)), Math.ceil(Math.max(...prices))];
}

const inRange = (minute: number | null, range: DayMinuteRange): boolean =>
  isFullDay(range) || (minute !== null && minute >= range[0] && minute <= range[1]);

/** Normaliza o código digitado: caixa alta, sem espaços — "la 2084" ≡ "LA2084". */
const normalizeFlightCode = (raw: string): string => raw.toUpperCase().replace(/\s+/g, '');

/**
 * Match EXATO do número do voo (#2110): o código digitado casa
 * companhia+número ("LA2084") ou só o número ("2084" pega qualquer companhia)
 * de QUALQUER segmento do voo (ida e volta). Exato, não substring: o eixo
 * responde à pergunta "onde está o voo X", não a um autocomplete.
 */
const matchesFlightNumber = (o: FlightOfferSource, code: string): boolean =>
  allSegments(o).some((s) => {
    const num = normalizeFlightCode(s.flightNumber);
    return num === code || `${s.carrier.toUpperCase()}${num}` === code;
  });

const passesShape = (o: FlightOfferSource, st: FlightFilterState): boolean => {
  if (st.stops.length > 0 && !st.stops.some((k) => STOPS_PRED[k](stopsOf(o)))) return false;
  if (st.airlines.length > 0 && !carriersOf(o).some((c) => st.airlines.includes(c))) return false;
  if (st.airports.length > 0 && !airportsOf(o).some((a) => st.airports.includes(a))) return false;
  if (st.avoidAirports.length > 0 && connectionsOf(o).some((a) => st.avoidAirports.includes(a)))
    return false;
  if (st.viaAirports.length > 0 && !connectionsOf(o).some((a) => st.viaAirports.includes(a)))
    return false;
  const code = normalizeFlightCode(st.flightNumber);
  if (code !== '' && !matchesFlightNumber(o, code)) return false;
  return true;
};

const passesBudget = (o: FlightOfferSource, st: FlightFilterState): boolean => {
  if (st.withinPolicyOnly && o.policy?.status !== 'in') return false;
  if (priceOf(o) > st.maxPrice || priceOf(o) < st.minPrice) return false;
  return inRange(depOf(o), st.departureRange) && inRange(arrOf(o), st.arrivalRange);
};

/** Só os cortes, PRESERVANDO a ordem recebida — é o que a página vinda do servidor
 *  pede: ela já vem ordenada pelo provider sobre o conjunto inteiro, e reordenar
 *  aqui jogaria fora justamente a informação que a página traz. */
export function filterOffers<O extends FlightOfferSource>(
  offers: readonly O[],
  st: FlightFilterState,
): O[] {
  return offers.filter((o) => passesShape(o, st) && passesBudget(o, st));
}

/** Lista exibida = ofertas que passam nos filtros, ordenadas pelo critério. */
export function filterAndSortOffers<O extends FlightOfferSource>(
  offers: readonly O[],
  st: FlightFilterState,
): O[] {
  const filtered = filterOffers(offers, st);
  // `rec` ordena a BASE por preço crescente (o resto da lista recomendada é
  // preço, por contrato do desenho — issue #2108); quem põe os destacados na
  // frente é a composição do host (`composeRecommended`), não este comparador.
  const cmp: Record<SortKey, (a: O, b: O) => number> = {
    rec: (a, b) => priceOf(a) - priceOf(b),
    price: (a, b) => priceOf(a) - priceOf(b),
    dep: (a, b) => (depOf(a) ?? Infinity) - (depOf(b) ?? Infinity),
    dur: (a, b) => durOf(a) - durOf(b),
    arr: (a, b) => (arrOf(a) ?? Infinity) - (arrOf(b) ?? Infinity),
  };
  return [...filtered].sort(cmp[st.sort]);
}

/** A ordem dos papéis na frente da lista recomendada — o desenho do #2108:
 *  1º Recomendado, 2º Menor preço, 3º Menos tempo. */
const RECOMMENDED_ROLES: ReadonlyArray<'best' | 'cheap' | 'fast'> = ['best', 'cheap', 'fast'];

/**
 * A COMPOSIÇÃO da lista "Recomendado" (#2108): os destacados pelos badges do
 * fio vêm pra frente — Recomendado, Menor preço, Menos tempo, nessa ordem — e o
 * resto fica NA ORDEM RECEBIDA (a do provider quando a página é dele; a local
 * por preço quando não — `serverOrder('rec')` pede CHEAPER exatamente pra esse
 * resto ser preço crescente). Um card que acumula papéis ocupa UMA posição: o
 * primeiro papel que o encontra o leva, e o papel seguinte cai no próximo
 * portador — nunca duplica.
 */
// Mutável de propósito: sem destacados devolve a MESMA lista (identidade estável
// para quem memoiza sobre ela).
export function composeRecommended<O extends FlightOfferSource>(offers: O[]): O[] {
  const front: O[] = [];
  for (const role of RECOMMENDED_ROLES) {
    const found = offers.find((o) => !front.includes(o) && o.badges?.some((b) => b.kind === role));
    if (found) front.push(found);
  }
  if (front.length === 0) return offers;
  return [...front, ...offers.filter((o) => !front.includes(o))];
}

/** Os eixos do painel que podem estar cortando — o vocabulário do "por quê a
 *  lista zerou" (#3239 C5). Sai + Chega são UM eixo (`time`), mín + máx de
 *  preço também (`price`), e os chips de conexão (evitar/exigir) são
 *  `connections`, como no protótipo. */
export type FilterAxis =
  | 'stops'
  | 'airlines'
  | 'airports'
  | 'time'
  | 'policy'
  | 'price'
  | 'flightNumber'
  | 'connections';

/** Quais eixos estão cortando alguma coisa, na ORDEM do painel. `floor`/`ceiling`
 *  são os do CONJUNTO da busca; sem eles cai na lista em mão. É a régua do
 *  "Redefinir" (`activeAxisCount`) e do estado vazio que nomeia o culpado. */
export function activeAxes(
  offers: readonly FlightOfferSource[],
  st: FlightFilterState,
  ceiling?: number,
  floor?: number,
): FilterAxis[] {
  const axes: FilterAxis[] = [];
  if (st.stops.length > 0) axes.push('stops');
  if (st.airlines.length > 0) axes.push('airlines');
  if (st.airports.length > 0) axes.push('airports');
  if (!isFullDay(st.departureRange) || !isFullDay(st.arrivalRange)) axes.push('time');
  if (st.withinPolicyOnly) axes.push('policy');
  if (st.maxPrice < ceilingOf(offers, ceiling) || st.minPrice > floorOf(offers, floor)) {
    axes.push('price');
  }
  if (st.flightNumber.trim() !== '') axes.push('flightNumber');
  if (st.avoidAirports.length > 0 || st.viaAirports.length > 0) axes.push('connections');
  return axes;
}

/** Quantos eixos estão cortando alguma coisa — alimenta o disabled do
 *  "Redefinir". Ver `activeAxes`. */
export function activeAxisCount(
  offers: readonly FlightOfferSource[],
  st: FlightFilterState,
  ceiling?: number,
  floor?: number,
): number {
  return activeAxes(offers, st, ceiling, floor).length;
}

/** O que cada opção custa antes do clique: quantos voos ela deixa e o mais
 *  barato entre eles (`null` quando zera). */
export type OptionCount = { n: number; from: number | null };

const countOf = (offers: readonly FlightOfferSource[]): OptionCount => ({
  n: offers.length,
  from: offers.length === 0 ? null : Math.min(...offers.map(priceOf)),
});

/**
 * Dois eixos com contagem, os dois apurados na PÁGINA em mão: aeroporto e paradas.
 *
 * AEROPORTO sempre foi honesto de graça. O `flightSearch` não tem campo de
 * aeroporto, então o eixo corta na página desde o começo — contar na página é
 * contar no escopo dele, não uma amostra se passando por inventário.
 *
 * PARADAS voltou a contar no #2111 ("Voo direto · R$ 348"), mas sobre a página:
 * a contagem de CONJUNTO custava uma busca keyed própria por opção a cada refine
 * (o `searchSize` responde pelo filtro que você manda, e o provider não agrega).
 * O menor preço da página é um limite inferior honesto do menor do conjunto sob
 * ordem CHEAPER (a página 1 contém o mais barato do conjunto filtrado).
 *
 * HORÁRIO nunca teve: a contagem de CHEGADA só existiria sobre a página, e as duas
 * metades saem juntas pra o eixo ler como um eixo só.
 *
 * COMPANHIA nunca teve: seria uma pergunta por linha ao provider, e o teto que
 * existia fazia a linha herdar o total da busca — ver `AirlineSection`.
 */
export type FlightFilterOptionCounts = {
  airports: Record<string, OptionCount>;
  stops: Record<StopsChoice, OptionCount>;
};

/**
 * Contagem por opção, apurada sobre a lista filtrada pelos OUTROS eixos (padrão
 * TravelPerk do protótipo): o número de GRU sai com os cortes ativos mas sem o
 * corte de aeroporto; o preço de "Voo direto" sai sem o corte de paradas.
 * `n: 0` fica visível e desabilitado na UI, nunca some.
 */
export function optionCounts(
  offers: readonly FlightOfferSource[],
  st: FlightFilterState,
  cityOf: CityOfAirport,
): FlightFilterOptionCounts {
  const byAirport = filterAndSortOffers(offers, { ...st, airports: [] });
  const byStops = filterAndSortOffers(offers, { ...st, stops: [] });

  return {
    airports: Object.fromEntries(
      airportOptions(offers, cityOf)
        .flatMap((g) => g.airports)
        .map((iata) => [iata, countOf(byAirport.filter((o) => airportsOf(o).includes(iata)))]),
    ),
    stops: Object.fromEntries(
      STOPS_CHOICES.map((k) => [k, countOf(byStops.filter((o) => STOPS_PRED[k](stopsOf(o))))]),
    ) as Record<StopsChoice, OptionCount>,
  };
}

/**
 * Os cortes que o SERVIDOR aplica, derivados do estado do painel. O tipo vem do
 * fio (`filter_wire`, alias do contrato do domain).
 *
 * `arrivalPeriods` e `airports` NÃO entram: o filtro do provider não tem campo de
 * chegada nem de aeroporto, então esses dois seguem cortando aqui, sobre a página
 * devolvida. `maxPrice` só viaja quando é corte de verdade (abaixo do teto da
 * faixa); no teto ele não filtra nada e faria a busca parecer cortada.
 */
/**
 * A UNIÃO das opções de paradas → o teto único que o fio aceita. Os baldes "até N"
 * são encaixados (direto ⊂ até-1 ⊂ até-2), então a união deles é o MAIOR teto
 * marcado. `more_than_2` não é teto: com ele na seleção nenhum corte viaja e o
 * eixo inteiro corta no cliente, sobre a página (o `filterOffers` local apara).
 */
function stopsCut(stops: StopsChoice[]): NonNullable<FlightServerCuts['stops']> | undefined {
  if (stops.length === 0 || stops.includes('more_than_2')) return undefined;
  if (stops.includes('up_to_2')) return 'up_to_2';
  if (stops.includes('up_to_1')) return 'up_to_1';
  return 'nonstop';
}

/** Critério de ordenação do painel → o vocabulário do fio.
 *
 *  `rec` pede CHEAPER (não `recommended`) DE PROPÓSITO: a lista recomendada do
 *  desenho é [recomendado, mais barato, mais rápido] + o RESTO em preço
 *  crescente (#2108) — os três primeiros saem dos badges do fio e o resto é a
 *  própria página do provider, que só é preço crescente se o pedido for
 *  CHEAPER. O `recommended` do provider ordenaria o resto por um critério
 *  opaco que o desenho não pediu. */
export function serverOrder(sort: SortKey): FlightServerOrder {
  if (sort === 'dur') return 'fastest';
  if (sort === 'dep') return 'earliest';
  if (sort === 'arr') return 'earlier_arrival';
  return 'cheapest';
}

export function serverCuts(
  st: FlightFilterState,
  priceCeiling: number,
  priceFloor = 0,
): FlightServerCuts {
  const stops = stopsCut(st.stops);
  // O piso só é corte acima do piso da BUSCA, e nunca viaja sozinho: o
  // `priceRange` do provider é uma faixa fechada, então o teto vai junto mesmo
  // quando encostado no teto do conjunto (ali ele é identidade).
  const minCut = st.minPrice > priceFloor;
  // Só os DÍGITOS viajam ("LA 2084" → "2084"): o `number` do provider é o
  // número do voo, e a companhia do código fica no corte exato do cliente.
  // Acima de 6 dígitos NADA viaja: o fio é `/^\d{1,6}$/` e mandar mais
  // derrubaria o refine inteiro com 400 (todos os eixos degradariam pro
  // cliente); truncar mudaria o voo pedido. O corte exato do cliente segue
  // valendo — e um código de 7+ dígitos não casa voo nenhum de todo jeito.
  const allDigits = st.flightNumber.replace(/\D+/g, '');
  const digits = allDigits.length <= 6 ? allDigits : '';
  return {
    ...(stops ? { stops } : {}),
    ...(st.airlines.length > 0 ? { airlines: st.airlines } : {}),
    ...(minCut ? { minPrice: st.minPrice } : {}),
    ...(st.maxPrice < priceCeiling || minCut ? { maxPrice: st.maxPrice } : {}),
    ...(digits ? { number: digits } : {}),
    // A régua produz UMA janela arbitrária — o fio sempre aceitou HH:mm livre.
    ...(isFullDay(st.departureRange)
      ? {}
      : {
          departureWindows: [
            {
              start: formatDayMinute(st.departureRange[0]),
              end: formatDayMinute(st.departureRange[1]),
            },
          ],
        }),
    ...(st.withinPolicyOnly ? { withinPolicyOnly: true } : {}),
    ...(st.avoidAirports.length > 0 ? { avoidAirports: st.avoidAirports } : {}),
    ...(st.viaAirports.length > 0 ? { viaAirports: st.viaAirports } : {}),
  };
}

/**
 * O teto de política DECLARADO pelas ofertas desta lista (`policy.ceiling`).
 *
 * Antes o número era inferido — o preço da oferta FORA da política mais barata da
 * página em mão. Com paginação (uma busca traz centenas de opções) isso anunciava
 * um valor arbitrário como se fosse o limite da empresa: o relato de #2457 é
 * exatamente "Acima de R$ 6.542 está fora da política" numa conta sem teto de
 * R$ 6.542 nenhum. Sem o dado da política, nenhum número — a linha some.
 *
 * `undefined` também quando a lista traz MAIS DE UM teto (o cap é por classe/rota:
 * não existe um número honesto pro cabeçalho) ou quando a moeda do teto não é a dos
 * preços exibidos — cap em USD ao lado de preço em BRL erra por ~5x, e converter é
 * decisão de quem tem a cotação, não daqui.
 */
export function policyCeiling(
  offers: readonly FlightOfferSource[],
): { amount: number; currency: string } | undefined {
  const declared = new Map<string, { amount: number; currency: string }>();
  for (const o of offers) {
    const ceiling = o.policy?.ceiling;
    if (ceiling) declared.set(`${String(ceiling.amount)}|${ceiling.currency}`, ceiling);
  }
  const [ceiling] = [...declared.values()];
  if (!ceiling || declared.size > 1) return undefined;
  return offers.every((o) => o.price.currency === ceiling.currency) ? ceiling : undefined;
}

/**
 * Companhias presentes com a faixa de preço de cada uma, ordenadas pt-BR pelo
 * nome — escolher companhia às cegas é o filtro mais caro de desfazer; com a
 * faixa na linha o corte é informado antes de acontecer.
 */
export function airlineOptions(
  offers: readonly FlightOfferSource[],
): Array<{ iata: string; name: string; from: number; to: number }> {
  const map = new Map<string, { name: string; prices: number[] }>();
  for (const o of offers) {
    for (const iata of carriersOf(o)) {
      const seg = allSegments(o).find((s) => s.carrier === iata);
      const name = (seg ? carrierNameOf(seg) : '') || iata;
      const cur = map.get(iata) ?? { name, prices: [] };
      cur.prices.push(priceOf(o));
      map.set(iata, cur);
    }
  }
  return [...map]
    .map(([iata, { name, prices }]) => ({
      iata,
      name,
      from: Math.min(...prices),
      to: Math.max(...prices),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}

export type AirportGroup = { city: string; airports: string[] };

/**
 * Aeroportos da lista agrupados por CIDADE — autoridade `domain/geo`
 * (`cityOf`, o `cityByAirport` do host), nunca a rota do voo (na volta a partida é na outra ponta e
 * o aeroporto apareceria nos dois grupos). IATA fora do cadastro cai no grupo
 * sentinela `'other'` (a UI traduz o rótulo), sempre por último.
 */
export function airportOptions(
  offers: readonly FlightOfferSource[],
  cityOf: CityOfAirport,
): AirportGroup[] {
  const byCity = new Map<string, Set<string>>();
  for (const o of offers) {
    for (const iata of airportsOf(o)) {
      const city = cityOf(iata) ?? 'other';
      const set = byCity.get(city) ?? new Set<string>();
      set.add(iata);
      byCity.set(city, set);
    }
  }
  return [...byCity]
    .map(([city, set]) => ({ city, airports: [...set].sort((a, b) => a.localeCompare(b)) }))
    .sort((a, b) => {
      if (a.city === 'other') return 1;
      if (b.city === 'other') return -1;
      return a.city.localeCompare(b.city, 'pt-BR');
    });
}

/** Uma barra do histograma de preços: `height` 0–1 (relativa ao maior bucket),
 *  `on` quando o bucket toca o intervalo `[mín, máx]` selecionado. */
export interface PriceBar {
  height: number;
  on: boolean;
}

/**
 * Distribuição de preços em `bucketCount` faixas iguais sobre `[min,max]` da
 * lista — alimenta o histograma da régua de preço. Alturas normalizadas pelo
 * bucket mais cheio; barras cujo bucket toca `[minPrice, maxPrice]` ficam `on`
 * (a régua tem duas pontas, #2110). Vazio quando não há faixa (`max ≤ min`).
 */
export function priceHistogram(
  offers: readonly FlightOfferSource[],
  minPrice: number,
  maxPrice: number,
  bucketCount = 12,
): PriceBar[] {
  const [min, max] = priceBounds(offers);
  if (max <= min) return [];
  const span = max - min;
  const counts = Array.from({ length: bucketCount }, () => 0);
  for (const o of offers) {
    const idx = Math.max(
      0,
      Math.min(bucketCount - 1, Math.floor(((priceOf(o) - min) / span) * bucketCount)),
    );
    counts[idx] = (counts[idx] ?? 0) + 1;
  }
  const maxCount = Math.max(...counts);
  return counts.map((c, i) => {
    const start = min + (i / bucketCount) * span;
    const end = min + ((i + 1) / bucketCount) * span;
    return {
      height: maxCount ? c / maxCount : 0,
      on: start <= maxPrice && end >= minPrice,
    };
  });
}
